import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
  DEVICE_SEARCH_EXPORT_NAME,
  DEVICE_SEARCH_IMPORT_EXPORT_TEMPLATE_COLUMNS_ORDER,
  DEVICE_SEARCH_SORT_TIEBREAKER,
} from '@cdw-ae/cdw-grid-configs';
import {
  type DeviceAsset,
  ExecutionStatus,
  ExportPurpose,
  type ExportSidecarColumn,
  isTerminalExecutionStatus,
} from '@cdw-ae/common-data';
import type { BulkActionSkipGroup } from '@cdw-ae/common-ui-components-bulk-update-action';
import {
  buildExportDialogData,
  CdwExportDialogComponent,
  CdwExportForBulkEditDialogComponent,
  CdwImportDialogComponent,
  type ExportDialogData,
  type ExportForBulkEditDialogData,
  type ExportForBulkEditDialogResult,
  type ImportDialogResult,
} from '@cdw-ae/common-ui-components-dialog';
import type { CdwSsrmGridComponent } from '@cdw-ae/common-ui-components-search-grid';
import type { SelectedFilterField } from '@cdw-ae/filter-bar-interfaces';
import { selectJobExecutionByJobId } from '@cdw-ae/jobs-status-state';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { firstValueFrom, race } from 'rxjs';
import { filter, map, take, tap } from 'rxjs/operators';
import * as DrivePickerActions from '../../../+state/drive-picker/drive-picker.actions';
import { getExportSkipReasonLabel } from '../device-action-eligibility/skip-reason-export-labels';

/**
 * Identity bundle the consumer provides per call. Stateless service —
 * no per-instance subscription to user/customer selectors.
 *
 * EDUS-2520: `userName` is forwarded into the export email template's
 * "{USER_NAME} shared an export with you" header + "Created by: {USER_NAME}
 * <{USER_EMAIL}>" row. Required for the email's display-name rendering;
 * the share-export step (`apps/saas-jobs/job-steps/exports/share-export/
 * src/app/app.service.ts:128`) falls back to the email address when this
 * is missing, which produces the "<email> shared an export with you"
 * regression the QA hit list flagged.
 */
export interface DialogIdentity {
  /** Firebase UID (selection-context). NOT job tracking — see `dbUserId`. */
  fbUserId: string;
  dbUserId: number;
  userEmail: string;
  userName?: string;
  customerId: number;
}

/**
 * Common params shape across all three dialog-opening methods. The grid
 * reference is the consumer's @ViewChild — service degrades gracefully
 * when undefined (matches the legacy `grid?.getX() ?? []` pattern).
 *
 * EDUS-2486 Hit-list #7: SSRM consumers populate `selectedCount` +
 * `gridSessionId` + `viewKey` instead of `selectedIds` (which is empty
 * because selection lives in Redis on the server). When provided, the
 * dialogs send `useSelection: true` to the export endpoint and let
 * saas-device-api materialize the affected device id list at dispatch.
 * `selectedIds` is retained for backwards compatibility with
 * non-SSRM callers.
 */
export interface DeviceDialogParams {
  grid: CdwSsrmGridComponent<DeviceAsset> | undefined;
  filters: SelectedFilterField[];
  selectedIds: string[];
  identity: DialogIdentity;
  selectedCount?: number;
  gridSessionId?: string;
  viewKey?: string;
}

/**
 * Orchestrates the three device-search export/import dialogs:
 * `CdwExportDialogComponent`, `CdwExportForBulkEditDialogComponent`,
 * `CdwImportDialogComponent`. Extracted from `DeviceSearchComponent`
 * during the SSRM migration cleanup so the consumer stays focused on
 * grid coordination rather than dialog plumbing.
 *
 * Design: stateless orchestrator — every call accepts the grid reference,
 * current filters, current selection, and user identity as params. The
 * service holds no per-call state and is safe to share at the
 * `providedIn: 'root'` level.
 */
@Injectable({ providedIn: 'root' })
export class DeviceExportImportService {
  constructor(
    private dialog: MatDialog,
    private store: Store,
    private actions$: Actions
  ) {}

  /**
   * Open the human-readable export dialog. Defaults to
   * `ExportPurpose.HUMAN_READABLE`; pass `IMPORT_READY` for
   * the bulk-edit template variant (though most callers should
   * use `openExportForBulkEditDialog` for that).
   */
  openExportDialog(
    params: DeviceDialogParams & { purpose?: ExportPurpose }
  ): void {
    const { grid, filters, selectedIds, identity } = params;
    const purpose = params.purpose ?? ExportPurpose.HUMAN_READABLE;
    // EDUS-2486 Hit-list #7: derive scope/count from `selectedCount`
    // when SSRM consumers provide it; fall back to `selectedIds.length`
    // for legacy callers.
    const effectiveCount = params.selectedCount ?? selectedIds.length;
    const exportScope: 'selected' | 'all' =
      effectiveCount > 0 ? 'selected' : 'all';
    const useServerSideSelection =
      params.selectedCount !== undefined &&
      params.selectedCount > 0 &&
      !!params.gridSessionId &&
      !!params.viewKey;

    const dialogData = buildExportDialogData({
      columnOrder: grid?.getColumnOrder() ?? [],
      sortOrder: grid?.getSortOrder() ?? [],
      filters,
      selectedIds,
      searchItemTotal: grid?.getItemTotal() ?? 0,
      exportScope,
      purpose,
      queryType: 'device-search',
      customerId: identity.customerId,
      fbUserId: identity.fbUserId,
      dbUserId: identity.dbUserId,
      userEmail: identity.userEmail,
      userName: identity.userName,
      exportName: DEVICE_SEARCH_EXPORT_NAME,
      importReadyColumnOrder:
        DEVICE_SEARCH_IMPORT_EXPORT_TEMPLATE_COLUMNS_ORDER,
      sortTiebreaker: DEVICE_SEARCH_SORT_TIEBREAKER,
    });
    // Override the helper's `selectedCount` (which derives from
    // selectedIds.length, always 0 in SSRM) with the count we got
    // from the SSRM grid's state service.
    dialogData.selectedCount = effectiveCount;
    if (useServerSideSelection) {
      dialogData.useSelection = true;
      dialogData.viewKey = params.viewKey;
      dialogData.gridSessionId = params.gridSessionId;
    }

    this.dialog.open(CdwExportDialogComponent, {
      width: '500px',
      disableClose: true,
      data: dialogData,
    });
  }

  /**
   * Open the bulk-edit template export dialog. Always emits an
   * `IMPORT_READY` export. The dialog's "Return to Import" link closes
   * with `requestReturnToImport: true`, which re-opens the import
   * dialog with the same params.
   */
  openExportForBulkEditDialog(params: DeviceDialogParams): void {
    const { grid, filters, selectedIds, identity } = params;
    const columnOrder = grid?.getColumnOrder() ?? [];
    const sortOrder = grid?.getSortOrder() ?? [];
    const columnCount = columnOrder.filter(
      (c) =>
        !c.hide &&
        c.name !== 'selectCheckboxComponent' &&
        c.name !== 'previewIconComponent'
    ).length;

    const importReadyColumns =
      DEVICE_SEARCH_IMPORT_EXPORT_TEMPLATE_COLUMNS_ORDER;
    const sortWithTiebreaker = [...sortOrder];
    if (
      !sortWithTiebreaker.some(
        (s) => s.name === DEVICE_SEARCH_SORT_TIEBREAKER.name
      )
    ) {
      sortWithTiebreaker.push(DEVICE_SEARCH_SORT_TIEBREAKER);
    }

    // EDUS-2486 Hit-list #7: prefer SSRM `selectedCount` over the
    // legacy `selectedIds.length` (always 0 in SSRM); pass through
    // useSelection/viewKey/gridSessionId so the dialog can dispatch
    // server-side selection resolution.
    const effectiveCount = params.selectedCount ?? selectedIds.length;
    const useServerSideSelection =
      params.selectedCount !== undefined &&
      params.selectedCount > 0 &&
      !!params.gridSessionId &&
      !!params.viewKey;

    const dialogData: ExportForBulkEditDialogData = {
      totalItemCount: grid?.getItemTotal() ?? 0,
      selectedCount: effectiveCount,
      columnCount,
      gridState: {
        columnOrder: importReadyColumns,
        sortOrder: sortWithTiebreaker,
        filters,
        selectedRowIds: selectedIds.length > 0 ? selectedIds : undefined,
      },
      queryType: 'device-search',
      customerId: identity.customerId,
      fbUserId: identity.fbUserId,
      dbUserId: identity.dbUserId,
      userEmail: identity.userEmail,
      userName: identity.userName,
      exportName: `Export ${DEVICE_SEARCH_EXPORT_NAME}`,
      ...(useServerSideSelection
        ? {
            useSelection: true,
            viewKey: params.viewKey,
            gridSessionId: params.gridSessionId,
          }
        : {}),
    };

    const dialogRef = this.dialog.open(CdwExportForBulkEditDialogComponent, {
      width: '500px',
      disableClose: true,
      data: dialogData,
    });

    dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe((result: ExportForBulkEditDialogResult | undefined) => {
        if (result?.requestReturnToImport) {
          this.openImportDialog(params);
        }
      });
  }

  /**
   * Open the import dialog. Watches the import job's terminal status
   * via `selectJobExecutionByJobId` and resolves the dialog's
   * `onWaitForJobCompletion` callback with the job's `additionalJobData`.
   *
   * `onImportComplete` is the EDUS-2454 hook — fires when the job hits
   * `finished` (added in G3, accepted but unused in G2 for forward-compat).
   */
  openImportDialog(
    params: DeviceDialogParams & { onImportComplete?: () => void }
  ): void {
    const { grid, filters, selectedIds, identity } = params;
    const columnOrder = grid?.getColumnOrder() ?? [];
    const sortOrder = grid?.getSortOrder() ?? [];
    const columnCount = columnOrder.filter(
      (c) =>
        !c.hide &&
        c.name !== 'selectCheckboxComponent' &&
        c.name !== 'previewIconComponent'
    ).length;

    const importReadyColumns =
      DEVICE_SEARCH_IMPORT_EXPORT_TEMPLATE_COLUMNS_ORDER;
    const sortWithTiebreaker = [...sortOrder];
    if (
      !sortWithTiebreaker.some(
        (s) => s.name === DEVICE_SEARCH_SORT_TIEBREAKER.name
      )
    ) {
      sortWithTiebreaker.push(DEVICE_SEARCH_SORT_TIEBREAKER);
    }

    // EDUS-2486 Hit-list #7: prefer SSRM `selectedCount` over the
    // legacy `selectedIds.length` (always 0 in SSRM). Import doesn't
    // dispatch a filtered server-side query (the file IS the data), so
    // `selectedRowIds`/useSelection plumbing isn't needed here — only
    // the count for the "X devices selected" header copy.
    const effectiveCount = params.selectedCount ?? selectedIds.length;

    const dialogRef = this.dialog.open(CdwImportDialogComponent, {
      width: '600px',
      disableClose: true,
      data: {
        totalItemCount: grid?.getItemTotal() ?? 0,
        selectedCount: effectiveCount,
        columnCount,
        gridState: {
          columnOrder: importReadyColumns,
          sortOrder: sortWithTiebreaker,
          filters,
          selectedRowIds: selectedIds.length > 0 ? selectedIds : undefined,
        },
        queryType: 'device-search',
        customerId: identity.customerId,
        fbUserId: identity.fbUserId,
        userEmail: identity.userEmail,
        filePrefix: `Export ${DEVICE_SEARCH_EXPORT_NAME}`,
        onWaitForJobCompletion: (jobId: number) => {
          return this.store.select(selectJobExecutionByJobId(jobId)).pipe(
            filter(
              (job) =>
                job !== null && isTerminalExecutionStatus(job.executionStatus)
            ),
            take(1),
            // EDUS-2454: refresh the device grid the moment the import
            // job hits `finished`. Side effect runs BEFORE map() so it
            // fires whether or not the consumer subscribes to the
            // returned observable. Skipped on terminal failures so a
            // failed import doesn't trigger a pointless refresh.
            tap((job) => {
              if (job?.executionStatus === ExecutionStatus.finished) {
                params.onImportComplete?.();
              }
            }),
            map((job) => {
              if (!job) throw new Error('Job not found');
              if (job.executionStatus === ExecutionStatus.finished) {
                return (job.additionalJobData ?? {}) as Record<string, unknown>;
              }
              const error = new Error(job.errorMessage ?? 'Import failed');
              (
                error as Error & {
                  additionalJobData?: Record<string, unknown>;
                }
              ).additionalJobData = (job.additionalJobData ?? {}) as Record<
                string,
                unknown
              >;
              throw error;
            })
          );
        },
        onOpenDrivePicker: () => {
          this.store.dispatch(DrivePickerActions.openDrivePicker());
          return firstValueFrom(
            race(
              this.actions$.pipe(
                ofType(DrivePickerActions.drivePickerFileSelected),
                take(1),
                map((action) => ({
                  fileId: action.file.fileId,
                  name: action.file.name,
                  mimeType: action.file.mimeType,
                }))
              ),
              this.actions$.pipe(
                ofType(DrivePickerActions.drivePickerCancelled),
                take(1),
                map(() => null)
              ),
              this.actions$.pipe(
                ofType(DrivePickerActions.drivePickerError),
                take(1),
                map((action) => {
                  throw new Error(action.error);
                })
              )
            )
          );
        },
      },
    });

    dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe((result: ImportDialogResult | undefined) => {
        if (result?.requestExportTemplate) {
          this.openExportForBulkEditDialog(params);
        }
      });
  }

  /**
   * Open the skipped-devices export dialog (PR #880 EDUS-2163).
   *
   * Triggered by the bulk-update drawer's `(exportSkippedDevices)` event
   * after the eligibility evaluator filters items. We flatten the skip
   * groups into a per-device list with a single `errorReason` sidecar
   * column (PO-confirmed schema, EDUS-2163 AC13), then reuse
   * `CdwExportDialogComponent` with the skipped IDs treated as the
   * selection (`exportScope: 'all'` so the dialog doesn't offer a
   * "selected vs filtered" toggle — the export is the skip list itself,
   * scoped exactly).
   *
   * Lives here for locality with the other export dialogs; the consumer
   * (DeviceSearchComponent) calls this from a thin delegator instead of
   * holding the dialog plumbing inline.
   */
  openExportSkippedDevicesDialog(params: {
    skipGroups: BulkActionSkipGroup<DeviceAsset>[];
    actionType: string;
    timezone: string;
    identity: DialogIdentity;
    userName: string;
  }): void {
    const { skipGroups, actionType, identity, userName } = params;

    const errorReasonValues: Record<string, string> = {};
    const skippedDeviceIds: string[] = [];

    for (const group of skipGroups) {
      for (const device of group.items) {
        const id = String(device.id);
        skippedDeviceIds.push(id);
        errorReasonValues[id] = getExportSkipReasonLabel(
          actionType,
          group.reason
        );
      }
    }

    const sidecarColumns: ExportSidecarColumn[] = [
      {
        name: 'errorReason',
        displayName: 'Error Reason',
        values: errorReasonValues,
      },
    ];

    const fixedColumns = [
      { name: 'id', hide: false },
      { name: 'serialNumber', hide: false },
      { name: 'status', hide: false },
      { name: 'osVersion', hide: false },
      { name: 'orgUnitPath', hide: false },
    ];

    const actionLabel = actionType
      .split('-')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join('_');
    const dialogData: ExportDialogData = {
      gridState: {
        columnOrder: fixedColumns,
        selectedRowIds: skippedDeviceIds,
      },
      totalItemCount: skippedDeviceIds.length,
      exportScope: 'all',
      selectedCount: skippedDeviceIds.length,
      purpose: ExportPurpose.HUMAN_READABLE,
      queryType: 'skipped-devices',
      customerId: identity.customerId,
      fbUserId: identity.fbUserId,
      dbUserId: identity.dbUserId,
      userEmail: identity.userEmail,
      userName,
      exportName: 'Skipped Device List',
      dialogTitle: 'Export Skipped Device List',
      scopeLabel: `Exporting ${
        skippedDeviceIds.length
      } rows (selected) including ${
        fixedColumns.length + sidecarColumns.length
      } columns.`,
      hideScopeSelector: true,
      sidecarColumns,
    };

    this.dialog.open(CdwExportDialogComponent, {
      width: '500px',
      disableClose: true,
      data: {
        ...dialogData,
        exportName: `Skipped_Devices_${actionLabel}`,
      },
    });
  }

  /**
   * Open the audit-log-changes export dialog (EDUS-2698).
   *
   * Triggered from the audit-log job detail page's "Export" button. Unlike the
   * grid exports, the row set is derived entirely server-side by the
   * audit-log-changes export (create + run change-diff steps) from the source device-update job's config +
   * before/after snapshots — the frontend only supplies the source `auditLogId`,
   * user identity, and the file/format/recipient choices the dialog collects.
   *
   * We reuse `CdwExportDialogComponent` with a fixed 6-column `gridState` (so the
   * cell-count estimate is sensible) and `hideScopeSelector: true` (there is no
   * "selected vs filtered" toggle — the scope is the audit log's changes).
   */
  openAuditLogChangesExportDialog(params: {
    auditLogId: number;
    identity: DialogIdentity;
    totalItemCount?: number;
    exportName?: string;
  }): void {
    const { auditLogId, identity } = params;

    const fixedColumns = [
      { name: 'deviceId', hide: false },
      { name: 'serialNumber', hide: false },
      { name: 'assetName', hide: false },
      { name: 'changedFields', hide: false },
      { name: 'before', hide: false },
      { name: 'after', hide: false },
    ];

    const dialogData: ExportDialogData = {
      gridState: { columnOrder: fixedColumns },
      totalItemCount: params.totalItemCount ?? 0,
      exportScope: 'all',
      selectedCount: 0,
      purpose: ExportPurpose.HUMAN_READABLE,
      queryType: 'audit-log-changes',
      customerId: identity.customerId,
      fbUserId: identity.fbUserId,
      dbUserId: identity.dbUserId,
      userEmail: identity.userEmail,
      userName: identity.userName,
      exportName: params.exportName ?? 'Device Changes',
      dialogTitle: 'Export Device Changes',
      hideScopeSelector: true,
      auditLogId,
    };

    this.dialog.open(CdwExportDialogComponent, {
      width: '500px',
      disableClose: true,
      data: dialogData,
    });
  }

  /**
   * Open the device-audit-history export dialog (EDUS-2699).
   *
   * Triggered from the device detail page (`device-summary`). The inverse of
   * {@link openAuditLogChangesExportDialog}: instead of one job's changes across
   * many devices, this exports ONE device's complete audit-change history across
   * every historical job that touched it — one before/after diff row per change
   * event. The row set is derived entirely server-side (the
   * device-audit-history export steps query the asset's history and diff each
   * source job's snapshots); the frontend only supplies the device's internal
   * `assetId` (uuid), user identity, and the file/format/recipient choices.
   *
   * Reuses `CdwExportDialogComponent` with a fixed 6-column `gridState` and
   * `hideScopeSelector: true` (the scope is the device's history — no
   * selected-vs-filtered toggle).
   */
  openDeviceAuditHistoryExportDialog(params: {
    /** Internal asset uuid (asset_devices.id) — the device-summary route id. */
    deviceId: string;
    identity: DialogIdentity;
    totalItemCount?: number;
    exportName?: string;
  }): void {
    const { deviceId, identity } = params;

    const fixedColumns = [
      { name: 'date', hide: false },
      { name: 'action', hide: false },
      { name: 'user', hide: false },
      { name: 'changedFields', hide: false },
      { name: 'before', hide: false },
      { name: 'after', hide: false },
    ];

    const dialogData: ExportDialogData = {
      gridState: { columnOrder: fixedColumns },
      totalItemCount: params.totalItemCount ?? 0,
      exportScope: 'all',
      selectedCount: 0,
      purpose: ExportPurpose.HUMAN_READABLE,
      queryType: 'device-audit-history',
      customerId: identity.customerId,
      fbUserId: identity.fbUserId,
      dbUserId: identity.dbUserId,
      userEmail: identity.userEmail,
      userName: identity.userName,
      exportName: params.exportName ?? 'Device Audit History',
      dialogTitle: 'Export Device Audit History',
      // The row count is only known server-side, so the default
      // "Exporting N items" label would read "Exporting 0 items".
      scopeLabel: "Exporting this device's full change history.",
      hideScopeSelector: true,
      assetId: deviceId,
      assetTypeId: 1,
    };

    this.dialog.open(CdwExportDialogComponent, {
      width: '500px',
      disableClose: true,
      data: dialogData,
    });
  }
}
